import { useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import type { OutreachStatus, NormalizedCategory } from "@hsh/shared";

export interface UrlFilters {
  search?: string;
  category?: NormalizedCategory;
  location?: string;
  minScore?: number;
  maxScore?: number;
  status?: OutreachStatus;
  noWebsite?: boolean;
  recentlyOpened?: boolean;
  lowReviewVolume?: boolean;
  noGoogleProfile?: boolean;
  contactedThisWeek?: boolean;
  lowFoodHygiene?: boolean;
  followUpDue?: boolean;
  noPhone?: boolean;
  notEnriched?: boolean;
  noCompaniesHouse?: boolean;
  poorGoogleRating?: boolean;
  // UI state
  place?: string; // Selected place ID
  view?: "list" | "filters";
  list?: boolean; // Separate list panel state (allows both filters and list to be open)
  page?: number;
}

// Keys that are boolean filters
const BOOLEAN_FILTER_KEYS = [
  "noWebsite",
  "recentlyOpened",
  "lowReviewVolume",
  "noGoogleProfile",
  "contactedThisWeek",
  "lowFoodHygiene",
  "followUpDue",
  "noPhone",
  "notEnriched",
  "noCompaniesHouse",
  "poorGoogleRating",
] as const;

// Keys that are numeric
const NUMERIC_FILTER_KEYS = ["minScore", "maxScore", "page"] as const;

/**
 * Parse URL search params into filter object
 */
export function parseUrlFilters(searchParams: URLSearchParams): UrlFilters {
  const filters: UrlFilters = {};

  // String filters
  const search = searchParams.get("search");
  if (search) filters.search = search;

  const category = searchParams.get("category");
  if (category) filters.category = category as NormalizedCategory;

  const location = searchParams.get("location");
  if (location) filters.location = location;

  const status = searchParams.get("status");
  if (status) filters.status = status as OutreachStatus;

  const place = searchParams.get("place");
  if (place) filters.place = place;

  const view = searchParams.get("view");
  if (view === "list" || view === "filters") filters.view = view;

  const list = searchParams.get("list");
  if (list === "true") filters.list = true;

  // Numeric filters
  for (const key of NUMERIC_FILTER_KEYS) {
    const value = searchParams.get(key);
    if (value !== null) {
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        (filters as any)[key] = num;
      }
    }
  }

  // Boolean filters
  for (const key of BOOLEAN_FILTER_KEYS) {
    if (searchParams.get(key) === "true") {
      (filters as any)[key] = true;
    }
  }

  return filters;
}

/**
 * Convert filter object to URL search params
 */
export function filtersToSearchParams(filters: UrlFilters): URLSearchParams {
  const params = new URLSearchParams();

  // String filters
  if (filters.search) params.set("search", filters.search);
  if (filters.category) params.set("category", filters.category);
  if (filters.location) params.set("location", filters.location);
  if (filters.status) params.set("status", filters.status);
  if (filters.place) params.set("place", filters.place);
  if (filters.view) params.set("view", filters.view);
  if (filters.list) params.set("list", "true");

  // Numeric filters
  for (const key of NUMERIC_FILTER_KEYS) {
    const value = (filters as any)[key];
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  }

  // Boolean filters (only set if true)
  for (const key of BOOLEAN_FILTER_KEYS) {
    if ((filters as any)[key] === true) {
      params.set(key, "true");
    }
  }

  return params;
}

/**
 * Hook to sync filters with URL search params
 */
export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);

  // Parse current URL into filters
  const filters = parseUrlFilters(searchParams);

  // Update URL with new filters (resets page to 1 unless page is explicitly set)
  const setFilters = useCallback(
    (newFilters: Partial<UrlFilters>, replace = false) => {
      setSearchParams(
        (prev) => {
          const currentFilters = parseUrlFilters(prev);
          const merged = replace
            ? (newFilters as UrlFilters)
            : { ...currentFilters, ...newFilters };

          // Reset page to 1 when filters change (unless page is explicitly being set)
          if (!("page" in newFilters)) {
            delete (merged as any).page;
          }

          // Remove undefined/null values
          Object.keys(merged).forEach((key) => {
            const value = (merged as any)[key];
            if (value === undefined || value === null || value === "") {
              delete (merged as any)[key];
            }
          });

          return filtersToSearchParams(merged);
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    // Preserve UI state when clearing filters
    const place = searchParams.get("place");
    const view = searchParams.get("view");
    const list = searchParams.get("list");
    const newParams = new URLSearchParams();
    if (place) newParams.set("place", place);
    if (view) newParams.set("view", view);
    if (list) newParams.set("list", list);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Set selected place
  const setSelectedPlace = useCallback(
    (placeId: string | null) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (placeId) {
            newParams.set("place", placeId);
          } else {
            newParams.delete("place");
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Set view mode (for filters panel)
  const setView = useCallback(
    (view: "list" | "filters" | null) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (view === "filters") {
            newParams.set("view", view);
          } else {
            newParams.delete("view");
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Set list panel visibility (independent of filters)
  const setListOpen = useCallback(
    (open: boolean) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (open) {
            newParams.set("list", "true");
          } else {
            newParams.delete("list");
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Set page
  const setPage = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (page > 1) {
            newParams.set("page", String(page));
          } else {
            newParams.delete("page");
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Extract just the data filters (not UI state)
  const dataFilters = {
    search: filters.search,
    category: filters.category,
    location: filters.location,
    minScore: filters.minScore,
    maxScore: filters.maxScore,
    status: filters.status,
    noWebsite: filters.noWebsite,
    recentlyOpened: filters.recentlyOpened,
    lowReviewVolume: filters.lowReviewVolume,
    noGoogleProfile: filters.noGoogleProfile,
    contactedThisWeek: filters.contactedThisWeek,
    lowFoodHygiene: filters.lowFoodHygiene,
    followUpDue: filters.followUpDue,
    noPhone: filters.noPhone,
    notEnriched: filters.notEnriched,
    noCompaniesHouse: filters.noCompaniesHouse,
    poorGoogleRating: filters.poorGoogleRating,
  };

  // Count active data filters
  const activeFilterCount = [
    filters.search,
    filters.category,
    filters.location,
    filters.minScore !== undefined && filters.minScore > 0,
    filters.maxScore !== undefined && filters.maxScore < 100,
    filters.status,
    filters.noWebsite,
    filters.recentlyOpened,
    filters.lowReviewVolume,
    filters.noGoogleProfile,
    filters.contactedThisWeek,
    filters.lowFoodHygiene,
    filters.followUpDue,
    filters.noPhone,
    filters.notEnriched,
    filters.noCompaniesHouse,
    filters.poorGoogleRating,
  ].filter(Boolean).length;

  return {
    filters,
    dataFilters,
    activeFilterCount,
    setFilters,
    clearFilters,
    setSelectedPlace,
    setView,
    setListOpen,
    setPage,
    selectedPlaceId: filters.place,
    view: filters.view,
    listOpen: filters.list || false,
    page: filters.page || 1,
    isInitialMount,
  };
}

/**
 * Generate a shareable URL for the current filter state
 */
export function generateShareableUrl(filters: UrlFilters): string {
  const params = filtersToSearchParams(filters);
  const baseUrl = window.location.origin + window.location.pathname;
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}
